import { IAlarm } from '@alert-manager/lib';
import React, { useState } from 'react';
import { acknowledgeAlarm, bulkAcknowledge, bulkResolve, resolveAlarm } from '../api/alarms';
import { useTheme } from '../contexts/ThemeContext';

const severityStrip: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  warning: 'bg-yellow-500',
  info: 'bg-blue-500',
};

const severityBadgeDark: Record<string, string> = {
  critical: 'bg-red-500/25 text-red-400 font-bold',
  high: 'bg-orange-500/25 text-orange-400 font-bold',
  warning: 'bg-yellow-500/25 text-yellow-300 font-bold',
  info: 'bg-blue-500/25 text-blue-400 font-bold',
};

const severityBadgeLight: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 font-bold',
  high: 'bg-orange-100 text-orange-700 font-bold',
  warning: 'bg-yellow-100 text-yellow-800 font-bold',
  info: 'bg-blue-100 text-blue-700 font-bold',
};

const envBadgeDark: Record<string, string> = {
  production: 'bg-red-500/20 text-red-400 ring-1 ring-red-500/30 font-bold',
  staging: 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/30 font-bold',
  develop: 'bg-cyan-500/20 text-cyan-400 ring-1 ring-cyan-500/30 font-bold',
};

const envBadgeLight: Record<string, string> = {
  production: 'bg-red-100 text-red-700 ring-1 ring-red-200 font-bold',
  staging: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200 font-bold',
  develop: 'bg-cyan-100 text-cyan-700 ring-1 ring-cyan-200 font-bold',
};

const formatDuration = (startTime: Date | string, endTime: Date | string): string => {
  const durationMs = new Date(endTime).getTime() - new Date(startTime).getTime();
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

const timeAgo = (date: Date | string): string => {
  const ms = Date.now() - new Date(date).getTime();
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
};

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface AlarmListProps {
  alarms: IAlarm[];
  onAlarmsUpdate?: () => void;
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;
}

interface ResolveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  alarmId: string;
}

const ResolveDialog: React.FC<ResolveDialogProps> = ({ isOpen, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(reason);
    setReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div
        className="p-5 rounded-lg shadow-2xl max-w-md w-full mx-4 border"
        style={{ backgroundColor: 'var(--surface-raised)', borderColor: 'var(--border)' }}
      >
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          Resolve Alarm
        </h3>
        <form onSubmit={handleSubmit}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Resolution reason (optional)..."
            className="w-full p-2.5 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500/50 resize-none"
            style={{
              backgroundColor: 'var(--input-bg)',
              borderColor: 'var(--input-border)',
              color: 'var(--text-primary)',
              border: '1px solid var(--input-border)',
            }}
            rows={3}
          />
          <div className="flex gap-2 justify-end mt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded transition-colors"
              style={{ color: 'var(--text-secondary)' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded transition-colors font-medium"
            >
              Resolve
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AlarmList: React.FC<AlarmListProps> = ({ alarms, pagination, onPageChange }) => {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [optimisticResolved, setOptimisticResolved] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resolveDialog, setResolveDialog] = useState<{
    isOpen: boolean;
    alarmId: string | null;
    bulk?: boolean;
  }>({ isOpen: false, alarmId: null });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const sevBadge = isDark ? severityBadgeDark : severityBadgeLight;
  const eBadge = isDark ? envBadgeDark : envBadgeLight;
  const defaultBadge = isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600';

  const handleResolveConfirm = async (reason: string) => {
    if (resolveDialog.bulk) {
      // Bulk resolve
      const ids = Array.from(selectedIds).filter((id) => !dismissedIds.has(id));
      if (ids.length === 0) return;
      setBulkProcessing(true);
      setOptimisticResolved((prev) => {
        const s = new Set(prev);
        ids.forEach((id) => s.add(id));
        return s;
      });
      setDismissedIds((prev) => {
        const s = new Set(prev);
        ids.forEach((id) => s.add(id));
        return s;
      });
      try {
        await bulkResolve(ids, reason || undefined);
        setSelectedIds(new Set());
      } catch (error) {
        console.error('Failed to bulk resolve:', error);
        setOptimisticResolved((prev) => {
          const s = new Set(prev);
          ids.forEach((id) => s.delete(id));
          return s;
        });
        setDismissedIds((prev) => {
          const s = new Set(prev);
          ids.forEach((id) => s.delete(id));
          return s;
        });
      } finally {
        setBulkProcessing(false);
      }
      return;
    }

    const alarmId = resolveDialog.alarmId;
    if (!alarmId || dismissedIds.has(alarmId)) return;
    // Optimistic: instantly show as resolved + dismissed (since resolve also marks as read)
    setOptimisticResolved((prev) => new Set(prev).add(alarmId));
    setDismissedIds((prev) => new Set(prev).add(alarmId));
    try {
      await resolveAlarm(alarmId, reason || undefined);
    } catch (error) {
      console.error('Failed to resolve alarm:', error);
      // Revert on failure
      setOptimisticResolved((prev) => {
        const s = new Set(prev);
        s.delete(alarmId);
        return s;
      });
      setDismissedIds((prev) => {
        const s = new Set(prev);
        s.delete(alarmId);
        return s;
      });
    }
  };

  const [optimisticAcked, setOptimisticAcked] = useState<Set<string>>(new Set());

  const handleAcknowledge = async (alarmId: string) => {
    if (optimisticAcked.has(alarmId)) return;
    setOptimisticAcked((prev) => new Set(prev).add(alarmId));
    try {
      await acknowledgeAlarm(alarmId);
    } catch (error) {
      console.error('Failed to acknowledge alarm:', error);
      setOptimisticAcked((prev) => {
        const s = new Set(prev);
        s.delete(alarmId);
        return s;
      });
    }
  };

  const handleBulkAcknowledge = async () => {
    const ids = Array.from(selectedIds).filter((id) => !optimisticAcked.has(id) && !dismissedIds.has(id));
    if (ids.length === 0) return;
    setBulkProcessing(true);
    setOptimisticAcked((prev) => {
      const s = new Set(prev);
      ids.forEach((id) => s.add(id));
      return s;
    });
    try {
      await bulkAcknowledge(ids);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to bulk acknowledge:', error);
      setOptimisticAcked((prev) => {
        const s = new Set(prev);
        ids.forEach((id) => s.delete(id));
        return s;
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = visibleAlarms.map((a) => a.id);
    const allSelected = visibleIds.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleIds));
    }
  };

  // Filter out optimistically dismissed alarms
  const visibleAlarms = alarms.filter((a) => !dismissedIds.has(a.id));
  const allSelected = visibleAlarms.length > 0 && visibleAlarms.every((a) => selectedIds.has(a.id));
  const someSelected = selectedIds.size > 0;

  if (visibleAlarms.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
          No alarms match your filters
        </div>
        <div className="text-xs mt-1" style={{ color: 'var(--text-muted)', opacity: 0.6 }}>
          Try adjusting the time range or filters
        </div>
      </div>
    );
  }

  const actionBtnClass = isDark
    ? 'px-2.5 py-1 text-[11px] font-medium rounded transition-colors disabled:opacity-50'
    : 'px-2.5 py-1 text-[11px] font-medium rounded transition-colors disabled:opacity-50';

  const readBtnClass = isDark
    ? `${actionBtnClass} text-slate-400 hover:text-white hover:bg-white/10`
    : `${actionBtnClass} text-slate-500 hover:text-slate-800 hover:bg-slate-100`;

  const resolveBtnClass = `${actionBtnClass} text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10`;

  const paginBtnClass = isDark
    ? 'px-2 py-1 text-xs rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-slate-300 hover:text-white hover:bg-white/10'
    : 'px-2 py-1 text-xs rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-slate-600 hover:text-slate-900 hover:bg-slate-100';

  return (
    <>
      {/* Bulk action bar */}
      <div
        className="flex items-center gap-3 mb-3 px-3 py-2 rounded-lg border transition-colors"
        style={{
          backgroundColor: someSelected ? (isDark ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.05)') : 'transparent',
          borderColor: someSelected ? (isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.15)') : 'transparent',
        }}
      >
        <button onClick={toggleSelectAll} className="flex items-center gap-2 cursor-pointer select-none group/sel">
          <div
            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
              allSelected
                ? 'bg-blue-500 border-blue-500'
                : someSelected
                  ? 'border-blue-400 bg-blue-500/30'
                  : isDark
                    ? 'border-slate-600 group-hover/sel:border-slate-400'
                    : 'border-slate-300 group-hover/sel:border-slate-400'
            }`}
          >
            {(allSelected || someSelected) && (
              <svg
                className="w-2.5 h-2.5 text-white"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {allSelected ? <polyline points="2 6 5 9 10 3" /> : <line x1="3" y1="6" x2="9" y2="6" />}
              </svg>
            )}
          </div>
          <span className="text-[11px] font-medium" style={{ color: someSelected ? (isDark ? '#93c5fd' : '#2563eb') : 'var(--text-muted)' }}>
            {someSelected ? `${selectedIds.size} selected` : 'Select all'}
          </span>
        </button>
        {someSelected && (
          <div className="flex items-center gap-1.5 ml-auto">
            <button
              onClick={handleBulkAcknowledge}
              disabled={bulkProcessing}
              className={`${actionBtnClass} ${isDark ? 'text-slate-300 hover:text-white hover:bg-white/10' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
            >
              Acknowledge {selectedIds.size}
            </button>
            <button
              onClick={() => setResolveDialog({ isOpen: true, alarmId: null, bulk: true })}
              disabled={bulkProcessing}
              className={`${actionBtnClass} text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10`}
            >
              Resolve {selectedIds.size}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-[11px] px-1.5 py-0.5 rounded transition-colors hover:bg-white/5"
              style={{ color: 'var(--text-muted)' }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {visibleAlarms.map((alarm) => {
          const isResolved = alarm.status === 'resolved' || optimisticResolved.has(alarm.id);
          const stripColor = isResolved ? (isDark ? 'bg-slate-600' : 'bg-slate-300') : severityStrip[alarm.severity] || 'bg-slate-500';

          const isExpanded = expandedId === alarm.id;

          return (
            <div
              key={alarm.id}
              className={`group rounded-lg border transition-all cursor-pointer ${selectedIds.has(alarm.id) ? (isDark ? 'ring-1 ring-blue-500/50' : 'ring-1 ring-blue-400/50') : ''}`}
              style={{
                backgroundColor: isResolved ? (isDark ? 'rgba(15,23,42,0.5)' : 'rgba(241,245,249,0.8)') : 'var(--card-bg)',
                borderColor:
                  alarm.acknowledgedStatus === 'unacknowledged' && !isResolved ? (isDark ? 'var(--border)' : '#cbd5e1') : 'var(--card-border)',
                opacity: isResolved ? 0.55 : 1,
              }}
              onClick={() => setExpandedId(isExpanded ? null : alarm.id)}
            >
              <div className="flex">
                {/* Severity strip */}
                <div className={`w-1.5 shrink-0 ${stripColor} ${isExpanded ? 'rounded-tl-lg' : 'rounded-l-lg'}`} />

                {/* Content */}
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelect(alarm.id);
                      }}
                      className="mt-0.5 shrink-0 group/cb"
                    >
                      <div
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                          selectedIds.has(alarm.id)
                            ? 'bg-blue-500 border-blue-500'
                            : isDark
                              ? 'border-slate-600 group-hover/cb:border-slate-400'
                              : 'border-slate-300 group-hover/cb:border-slate-400'
                        }`}
                      >
                        {selectedIds.has(alarm.id) && (
                          <svg
                            className="w-2.5 h-2.5 text-white"
                            viewBox="0 0 12 12"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="2 6 5 9 10 3" />
                          </svg>
                        )}
                      </div>
                    </button>
                    {/* Left: message + meta */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {alarm.acknowledgedStatus === 'unacknowledged' && !optimisticAcked.has(alarm.id) && (
                          <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                        )}
                        <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                          {alarm.description ?? alarm.message}
                        </h3>
                      </div>

                      {/* Meta row */}
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${sevBadge[alarm.severity] || defaultBadge}`}>
                          {alarm.severity}
                        </span>
                        <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${eBadge[alarm.environment] || defaultBadge}`}>
                          {alarm.environment}
                        </span>
                        {alarm.project && (
                          <span
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded ring-1 ${isDark ? 'bg-purple-500/20 text-purple-400 ring-purple-500/30' : 'bg-purple-100 text-purple-700 ring-purple-200'}`}
                          >
                            {alarm.project}
                          </span>
                        )}
                        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          via {alarm.source}
                        </span>
                        {(alarm.acknowledgedStatus === 'acknowledged' || optimisticAcked.has(alarm.id)) && !isResolved && (
                          <span className="text-[10px] text-amber-500 font-medium">
                            ack'd{alarm.acknowledgedBy ? ` by ${alarm.acknowledgedBy}` : ''}
                          </span>
                        )}
                        {isResolved && alarm.resolvedTime && (
                          <span className="text-[10px] text-emerald-500 font-medium">
                            resolved in {formatDuration(alarm.timestamp, alarm.resolvedTime)}
                            {alarm.resolvedBy && ` by ${alarm.resolvedBy}`}
                          </span>
                        )}
                        {alarm.resolutionReason && (
                          <span className="text-[10px] italic" style={{ color: 'var(--text-muted)' }}>
                            &mdash; {alarm.resolutionReason}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: time + actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      {alarm.acknowledgedStatus === 'unacknowledged' && !optimisticAcked.has(alarm.id) && alarm.status === 'ongoing' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcknowledge(alarm.id);
                          }}
                          className={readBtnClass}
                          title="Acknowledge alarm"
                        >
                          Acknowledge
                        </button>
                      )}
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {alarm.status === 'ongoing' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setResolveDialog({ isOpen: true, alarmId: alarm.id });
                            }}
                            className={resolveBtnClass}
                            title="Resolve"
                          >
                            Resolve
                          </button>
                        )}
                      </div>

                      <span
                        className="text-[10px] tabular-nums whitespace-nowrap font-medium"
                        style={{ color: 'var(--text-muted)' }}
                        title={new Date(alarm.timestamp).toLocaleString()}
                      >
                        {timeAgo(alarm.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded detail panel */}
              {isExpanded && (
                <div
                  className="border-t px-4 py-3"
                  style={{ borderColor: 'var(--border)', backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 mb-3">
                    <div>
                      <div className="text-[10px] uppercase font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>
                        Alarm ID
                      </div>
                      <div className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                        {alarm.id}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>
                        Source
                      </div>
                      <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {alarm.source}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>
                        Timestamp
                      </div>
                      <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {new Date(alarm.timestamp).toLocaleString()}
                      </div>
                    </div>
                    {!!(alarm as unknown as Record<string, unknown>).name && (
                      <div>
                        <div className="text-[10px] uppercase font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>
                          Name
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {String((alarm as unknown as Record<string, unknown>).name)}
                        </div>
                      </div>
                    )}
                    {alarm.project && (
                      <div>
                        <div className="text-[10px] uppercase font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>
                          Project
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {alarm.project}
                        </div>
                      </div>
                    )}
                    {alarm.acknowledgedBy && (
                      <div>
                        <div className="text-[10px] uppercase font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>
                          Acknowledged by
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {alarm.acknowledgedBy} at {alarm.acknowledgedAt ? new Date(alarm.acknowledgedAt).toLocaleString() : 'N/A'}
                        </div>
                      </div>
                    )}
                    {alarm.resolvedBy && (
                      <div>
                        <div className="text-[10px] uppercase font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>
                          Resolved by
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {alarm.resolvedBy} at {alarm.resolvedTime ? new Date(alarm.resolvedTime).toLocaleString() : 'N/A'}
                        </div>
                      </div>
                    )}
                    {alarm.resolutionReason && (
                      <div className="col-span-2 sm:col-span-3">
                        <div className="text-[10px] uppercase font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>
                          Resolution Reason
                        </div>
                        <div className="text-xs whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>
                          {alarm.resolutionReason}
                        </div>
                      </div>
                    )}
                    {!!alarm.links?.length && (
                      <div className="col-span-2 sm:col-span-3">
                        <div className="text-[10px] uppercase font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>
                          Investigate
                        </div>
                        <div className="text-xs flex flex-wrap gap-3">
                          {alarm.links.map((link) => (
                            <a
                              key={link.url}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`underline ${isDark ? 'text-blue-400' : 'text-blue-700'}`}
                            >
                              {link.label} ↗
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {!!alarm.originalMessage && (
                    <details className="mt-2">
                      <summary className="text-[10px] uppercase font-bold cursor-pointer select-none" style={{ color: 'var(--text-muted)' }}>
                        Original Payload
                      </summary>
                      <pre
                        className="mt-1.5 p-2.5 rounded text-[11px] overflow-auto max-h-64 font-mono"
                        style={{
                          backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)',
                          color: 'var(--text-secondary)',
                          border: '1px solid var(--border)',
                        }}
                      >
                        {JSON.stringify(alarm.originalMessage as object, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {pagination && onPageChange && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
          <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div className="flex gap-1">
            <button onClick={() => onPageChange(1)} disabled={!pagination.hasPrev} className={paginBtnClass}>
              First
            </button>
            <button onClick={() => onPageChange(pagination.page - 1)} disabled={!pagination.hasPrev} className={paginBtnClass}>
              Prev
            </button>

            {(() => {
              const pages = [];
              const current = pagination.page;
              const total = pagination.totalPages;
              let start = Math.max(1, current - 2);
              const end = Math.min(total, start + 4);
              if (end - start + 1 < 5) start = Math.max(1, end - 4);

              for (let i = start; i <= end; i++) {
                pages.push(
                  <button
                    key={i}
                    onClick={() => onPageChange(i)}
                    className={`px-2 py-1 text-xs rounded transition-colors font-medium ${
                      i === current ? (isDark ? 'bg-white/15 text-white' : 'bg-slate-200 text-slate-900') : paginBtnClass
                    }`}
                  >
                    {i}
                  </button>,
                );
              }
              return pages;
            })()}

            <button onClick={() => onPageChange(pagination.page + 1)} disabled={!pagination.hasNext} className={paginBtnClass}>
              Next
            </button>
            <button onClick={() => onPageChange(pagination.totalPages)} disabled={!pagination.hasNext} className={paginBtnClass}>
              Last
            </button>
          </div>
        </div>
      )}

      <ResolveDialog
        isOpen={resolveDialog.isOpen}
        onClose={() => setResolveDialog({ isOpen: false, alarmId: null })}
        onConfirm={handleResolveConfirm}
        alarmId={resolveDialog.bulk ? `${selectedIds.size} alarms` : resolveDialog.alarmId || ''}
      />
    </>
  );
};

export default AlarmList;
